import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Star, Lock } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { searchFoods } from "@/lib/foodlog";

const Kcal = ({ n }) => <span className="text-sm font-semibold shrink-0">{n?.calories ?? "—"}</span>;

export function FoodRow({ title, sub, nutrition, locked, onClick, testid }) {
  const { t } = useLang();
  return (
    <button onClick={onClick} disabled={locked} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-secondary/60 rounded-xl px-2 disabled:opacity-60" data-testid={testid}>
      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{title}</p>{sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}</div>
      {locked ? <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Lock className="h-3 w-3" />{t("j_locked")}</span> : <Kcal n={nutrition} />}
      <span className="text-xs text-muted-foreground">{t("kcal")}</span>
    </button>
  );
}

export function FoodSearch({ onFood, onRecipe, onCreate }) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const h = setTimeout(async () => { setLoading(true); try { setRes(await searchFoods(q.trim())); } catch { setRes({ foods: [], recipes: [], off_status: "unavailable" }); } finally { setLoading(false); } }, 400);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="space-y-3" data-testid="journal-search">
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input autoFocus placeholder={t("j_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl" data-testid="journal-search-input" /></div>
      <p className="text-xs text-muted-foreground">{t("j_search_hint")}</p>
      {loading && <p className="text-sm text-muted-foreground" data-testid="journal-searching">{t("j_searching")}</p>}
      {res?.off_status === "unavailable" && <p className="text-xs text-chenia-terracottadark" data-testid="journal-off-unavailable">{t("j_off_unavailable")}</p>}
      {res && !loading && res.foods.length + res.recipes.length === 0 && (
        <div className="text-sm text-muted-foreground" data-testid="journal-search-empty">{t("j_no_results")} · <button className="text-primary underline underline-offset-4" onClick={() => onCreate({ name: q })} data-testid="journal-search-create">{t("j_create_this")}</button></div>
      )}
      {res && <div className="divide-y divide-border max-h-[45vh] overflow-y-auto" data-testid="journal-search-results">
        {res.recipes.map((r) => <FoodRow key={r.recipe_id} title={r.name} sub={t("j_tab_recipes")} nutrition={r.nutrition} locked={r.locked} onClick={() => onRecipe(r)} testid={`journal-result-recipe-${r.recipe_id}`} />)}
        {res.foods.map((f) => <FoodRow key={f.food_id} title={f.name} sub={[f.brand, f.package_quantity].filter(Boolean).join(" · ") || t("j_per_100")} nutrition={f.per_100g} onClick={() => onFood(f)} testid={`journal-result-${f.food_id}`} />)}
      </div>}
    </div>
  );
}

export function RecentAndFavorites({ memberId, onFood, onRecent }) {
  const { t } = useLang();
  const { data: rec } = useQuery({ queryKey: ["food-recent", memberId], queryFn: async () => (await api.get("/food/recent", { params: { member_id: memberId } })).data.recent });
  const { data: fav } = useQuery({ queryKey: ["food-favorites"], queryFn: async () => (await api.get("/food/favorites")).data.favorites });
  const { data: mine } = useQuery({ queryKey: ["food-custom"], queryFn: async () => (await api.get("/food/custom")).data.foods });
  return (
    <div className="space-y-4 max-h-[55vh] overflow-y-auto" data-testid="journal-recent">
      <section><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("j_recent")}</p>
        {!rec?.length ? <p className="text-sm text-muted-foreground mt-1">{t("j_no_recent")}</p> : <div className="divide-y divide-border">{rec.map((r, i) => <FoodRow key={i} title={r.label} sub={`${r.quantity} ${r.unit === "portion" ? t("j_unit_portion") : r.unit}${r.brand ? ` · ${r.brand}` : ""}`} nutrition={r.nutrition} onClick={() => onRecent(r)} testid={`journal-recent-${i}`} />)}</div>}</section>
      <section><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1"><Star className="h-3 w-3" />{t("j_favorites")}</p>
        {!fav?.length ? <p className="text-sm text-muted-foreground mt-1">{t("j_no_fav")}</p> : <div className="divide-y divide-border">{fav.map((f) => <FoodRow key={f.food_id} title={f.name} sub={f.brand || t("j_per_100")} nutrition={f.per_100g} onClick={() => onFood(f)} testid={`journal-fav-${f.food_id}`} />)}</div>}</section>
      {mine?.length > 0 && <section><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("j_my_foods")}</p>
        <div className="divide-y divide-border">{mine.map((f) => <FoodRow key={f.food_id} title={f.name} sub={f.brand || t("j_per_100")} nutrition={f.per_100g} onClick={() => onFood(f)} testid={`journal-mine-${f.food_id}`} />)}</div></section>}
    </div>
  );
}

export function RecipePicker({ onRecipe }) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["food-recipes"], queryFn: async () => (await api.get("/food/recipes")).data.recipes });
  const list = (data || []).filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="journal-recipes">
      <Input placeholder={t("j_search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-xl" data-testid="journal-recipes-filter" />
      <p className="text-xs text-muted-foreground">{t("j_recipes_hint")}</p>
      <div className="divide-y divide-border max-h-[45vh] overflow-y-auto">{list.map((r) => <FoodRow key={r.recipe_id} title={r.name} nutrition={r.nutrition} locked={r.locked} onClick={() => onRecipe(r)} testid={`journal-recipe-${r.recipe_id}`} />)}</div>
    </div>
  );
}
